import { TaggedError } from 'better-result';
import * as z from 'zod';

export class ValidationError extends TaggedError('ValidationError')<{
  field: string;
  message: string;
  code?: string;
  // Set to true for state-transition or business-rule violations that the
  // OPIN/OFB regulatory specs return as 422 Unprocessable Entity instead of
  // 400 Bad Request. Default behavior remains 400 for shape validation.
  unprocessable?: boolean;
  // The underlying failure that produced this rejection (for example the JOSE error behind an
  // id_token check). Kept for diagnostics and conformance evidence; never rendered on the wire.
  cause?: Error;
}> {}

export class NotFoundError extends TaggedError('NotFoundError')<{
  resource: string;
  id: string;
}> {}

export class UnauthorizedError extends TaggedError('UnauthorizedError')<{
  reason: 'missing_token' | 'invalid_token' | 'expired_token' | 'insufficient_scope';
  message: string;
}> {}

export class ForbiddenError extends TaggedError('ForbiddenError')<{
  reason: string;
}> {}

export class ConflictError extends TaggedError('ConflictError')<{
  resource: string;
  message: string;
}> {}

export class InfrastructureError extends TaggedError('InfrastructureError')<{
  system: 'database' | 'cache' | 'events' | 'filesystem' | 'keycloak' | 'kong' | 'http' | 'fapi';
  message: string;
  cause?: Error;
  /**
   * A detail the CALLER may see, for the cases where the failure is a known,
   * hand-written gap rather than a subsystem fault.
   *
   * `message` can never reach the wire: a driver error carries the failing SQL
   * and every bound parameter, so `to-problem.ts` replaces it with a fixed
   * generic string. That is correct for a database or Keycloak fault, and wrong
   * for a typed stub whose whole purpose is to tell an operator WHICH
   * dependency was never configured — "temporarily unavailable, please retry"
   * sends them to retry a gap that no amount of retrying will close.
   *
   * Set this ONLY with text authored to be read by the caller: no interpolated
   * driver output, no query text, no user data. Leave it unset and the generic
   * string still applies, so a subsystem message cannot leak by omission. Same
   * split as `AuthError`'s `publicMessage` / `internalDetail`.
   */
  publicMessage?: string;
}> {}

export class EntitlementError extends TaggedError('EntitlementError')<{
  service: string;
  reason: 'not_licensed' | 'expired' | 'invalid_signature' | 'not_yet_valid';
}> {}

export const AuthErrorReason = z.enum([
  'missing_token',
  'malformed_token',
  'invalid_token',
  'cert_binding_mismatch',
  'cert_binding_required',
  'jwks_unavailable',
  'introspection_unavailable',
]);
export type AuthErrorReason = z.infer<typeof AuthErrorReason>;

const AUTH_UNAUTHORIZED = 401;
const AUTH_FORBIDDEN = 403;
const AUTH_UNAVAILABLE = 503;
export const AUTH_ERROR_STATUSES = [AUTH_UNAUTHORIZED, AUTH_FORBIDDEN, AUTH_UNAVAILABLE] as const;
export type AuthErrorStatus = (typeof AUTH_ERROR_STATUSES)[number];

export type AuthErrorInternalDetail = {
  readonly stage?: string;
  readonly status?: number;
};

export class AuthError extends TaggedError('AuthError')<{
  reason: AuthErrorReason;
  status: AuthErrorStatus;
  publicMessage: string;
  internalDetail?: AuthErrorInternalDetail;
}> {}

export type AppError =
  | ValidationError
  | NotFoundError
  | UnauthorizedError
  | ForbiddenError
  | ConflictError
  | InfrastructureError
  | EntitlementError
  | AuthError;
